import {
  type Institution,
  type ProviderUser,
  type Purchase,
  type NodeRoomV2,
} from '../../services';
import SegmentTracker from './trackers/segmentTracker';
import UserpilotTracker from './trackers/userpilotTracker';
import { UserPlanType } from '../../hooks/useUserSubscription/useUserSubscription.types';

export type Properties = Record<string, unknown>;

export type Tracker = {
  id: string;
  identify: (
    user?: ProviderUser,
    institution?: Institution,
    latestPurchase?: Purchase,
    rooms?: NodeRoomV2[],
    currentPlan?: UserPlanType,
  ) => void;
  track: (event: TrackedEvents, properties?: Properties) => Promise<void>;
  reset?: () => void;
};

export const TRACKERS: () => Tracker[] = () => [
  SegmentTracker,
  UserpilotTracker,
];

// Event values should be agreed with #intelligence-team as they will be tracked in Segment
export enum TrackedEvents {
  StartCall = 'Session Started',
  EndCall = 'Session Closed',
  TestPatientAdded = 'Test Patient Added',
  StartTestCall = 'Test Session Started',
  EndTestCall = 'Test Session Closed',
  CallRated = 'Call Rated',
  LiveChatMessageSent = 'Live Chat Message Sent',
  PrecallTestButtonClicked = 'Precall test button clicked',
  CallTroubleshootingInitiated = 'Troubleshooting Initiated',
  MediaAccessRequested = 'Media Access Requested',
  CheckInDeviceAccessTroubleshootingInitiated = 'Checkin Device Access Troubleshooting Initiated',
  CheckInStarted = 'Checkin Started',
  CheckInCompleted = 'Checkin Completed',
  CameraTested = 'Camera Tested',
  MicrophoneTested = 'Microphone Tested',
  SpeakerTested = 'Speaker Tested',
  NameEntered = 'Name Entered',
  RoomEntered = 'Room Entered',
  ButtonClicked = 'Button Clicked',
  ScreenshareStarted = 'Screenshare Started',
  ScreenshareEnded = 'Screenshare Ended',
  PatientInvited = 'Patient Invited',
  BackgroundEffectApplied = 'Background Effect Applied',

  /** When the file(s) upload begins. */
  FileTransferStarted = 'File Transfer Started',

  /** When the file(s) upload is completed (sender side). */
  FileTransferFinished = 'File Transfer Finished',

  /** When all of the files have been downloaded by the receiver. */
  FileTransferAccepted = 'File Transfer Accepted',

  /** Photo Capture Events */
  PhotoCaptureStarted = 'Photo Capture Started',
  PhotoCapturePhotoTaken = 'Photo Capture Photo Taken',
  PhotoCaptureRetakeClicked = 'Photo Capture Retake Clicked',
  PhotoCaptureDownloadClicked = 'Photo Capture Download Clicked',

  AvatarUploaded = 'Avatar Uploaded',

  /** Role Selection Events */
  ProviderOptionClicked = 'Provider Option Clicked',
  PatientOptionClicked = 'Patient Option Clicked',
  ProviderSelectionContinueClicked = 'Provider Selection Continue Clicked',
  PatientSelectionContinueClicked = 'Patient Selection Continue Clicked',

  UpgradeButtonClicked = 'Upgrade Button Clicked',
  UpgradeAbandonedDuringPlanOverviewPage = 'Upgrade Abandoned During Plan Overview Page',
  UpgradeAbandonedDuringPaymentSummaryPage = 'Upgrade Abandoned During Payment Summary Page',
  SubscriptionCycleChangedDuringPlanOverviewPage = 'Subscription Cycle Changed During Plan Overview Page',
  SubscriptionCycleChangedDuringPaymentSummaryPage = 'Subscription Cycle Changed During Payment Summary Page',
  DayPassTooltipViewedOnUpgradePlanOverviewPage = 'Day Pass Tooltip Viewed On Upgrade Plan Overview Page',
  DayPassPlanSelected = 'Day Pass Plan Selected',
  UpgradePlanSelectedToMoveToPaymentSummaryPage = 'Upgrade Plan Selected To Move To Payment Summary Page',
  BackButtonClickedOnPaymentSummaryPage = 'Back Button Clicked on Payment Summary Page',
  UpgradePromoCodeAddedSuccessfully = 'Upgrade Promo Code Added Successfully',
  UpgradePromoCodeSelectedInvalid = 'Invalid Upgrade Promo Code Selected',
  UpgradePromoCodeRemoved = 'Upgrade Promo Code Removed',
  UpgradeRecaptchaCompletedSuccessfully = 'Upgrade Recaptcha Completed Successfully',
  UpgradeRecaptchaFailed = 'Upgrade Recaptcha Failed',
  SubscribeButtonClickedToMoveToConfirmationPage = 'Subscribe Button Clicked To Move To Confirmation Page',
  StripeSourceAddedSuccessfullyDuringUpgrade = 'Stripe Source Added Successfully During Upgrade',
  StripeSourceFailedToAddDuringUpgrade = 'Stripe Source Failed to Add During Upgrade',
  UpgradePaymentInitiatedSuccessfully = 'Upgrade Payment Initiated Successfully',
  UpgradePaymentFailedToInitiate = 'Upgrade Payment Failed To Initiate',
  UpgradePaymentSuccessfullyConfirmed = 'Upgrade Payment Successfully Confirmed',
  UpgradePaymentFailedToConfirm = 'Upgrade Payment Failed to Confirm',
  UpgradePaymentCompletedSuccessfully = 'Upgrade Payment Completed Successfully',
  UpgradePaymentFailedToComplete = 'Upgrade Payment Failed to Complete',
  UpgradeFlowCompleted = 'Upgrade Flow Completed',
  ClinicSetupCompleted = 'Clinic Setup Completed',
  ClinicSetupAbandoned = 'Clinic Setup Abandoned',
  SeatsTooltipOnUpgradePaymentSummaryPageViewed = 'Seats Tooltip On Upgrade Payment Summary Page Viewed',
  SetupFeeTooltipOnUpgradePaymentSummaryPageViewed = 'Setup Fee Tooltip On Upgrade Payment Summary Page Viewed',
  SalesTaxTooltipOnUpgradePaymentSummaryPageViewed = 'Sales Tax Tooltip On Upgrade Payment Summary Page Viewed',

  // apps
  AppsPageVisited = 'Apps Page Visited',
  AppCardClicked = 'App Card Clicked',
  AppInstallClicked = 'App Install Clicked',
  AppInstallSuccessful = 'App Install Successful',
  AppRemoveClicked = 'App Remove Clicked',
  AppRemoveSuccessful = 'App Remove Successful',
  AppInformationClicked = 'App Information Clicked',
  PrototypesPageVisited = 'Prototypes Page Visited',
  AppLibrarySortChanged = 'App Library Sort Changed',
  AccessPrototypesModalOpened = 'Access Prototypes Modal Opened',
  AccessPrototypesModalClosed = 'Access Prototypes Modal Closed',
  AccessPrototypesOptInCompleted = 'Access Prototypes Opt-In Completed',

  /** Custom Waiting Room */
  WaitingRoomUpdated = 'Waiting Room Updated',

  // Pause Call Events
  CallHoldLaunched = 'Call Hold Launched',
  CallHoldResumed = 'Call Hold Resumed',

  ZoomSliderChanged = 'Zoom Slider Changed',

  FullscreenToggled = 'Fullscreen Toggled',
  PictureInPictureToggled = 'Picture In Picture Toggled',

  // Performance
  HighCpuDetected = 'High CPU Detected',

  SignUpFreeEmailEntered = 'Sign Up Free Email Entered',
  SignUpFreeOnboardingComplete = 'Sign Up Free Onboarding Complete',
  SignUpProEmailEntered = 'Sign Up Pro Email Entered',
  SignUpProOnboardingComplete = 'Sign Up Pro Onboarding Complete',
  SignUpProPaymentComplete = 'Sign Up Pro Payment Complete',
  SignIn = 'Sign In',
}

export const identify = (
  user?: ProviderUser,
  institution?: Institution,
  latestPurchase?: Purchase,
  rooms?: NodeRoomV2[],
  currentPlan?: UserPlanType,
): void => {
  void Promise.all(
    TRACKERS().map(async (tracker) => {
      tracker.identify(user, institution, latestPurchase, rooms, currentPlan);
    }),
  );
};

export const track = async (
  event: TrackedEvents,
  properties?: Properties,
): Promise<void> => {
  await Promise.all(
    TRACKERS().map(async (tracker) => tracker.track(event, properties)),
  );
};

export const getAnonymousId = (): string | undefined => {
  const { analytics } = window;
  return analytics?.user?.() ? analytics.user().anonymousId() : undefined;
};

export const reset = (): void => {
  TRACKERS().forEach((tracker) => {
    if (tracker.reset) {
      tracker.reset();
    }
  });
};
